import { useNavigate } from "react-router-dom"
import { ArrowLeft, Mail, Shield, CheckCircle2 } from "lucide-react"
import { Button, Card, CardBody, CardHeader, Divider } from "@heroui/react"
import { useAuth } from "@src/presentation/contexts/AuthContext"

export default function SecurityPage() {
    const navigate = useNavigate()
    const { user } = useAuth()

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                isIconOnly
                                variant="light"
                                onPress={() => navigate(-1)}
                                className="flex-shrink-0"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <h1 className="text-xl font-semibold text-foreground">Security Settings</h1>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-3xl mx-auto space-y-6">
                    {/* OTP Authentication */}
                    <Card className="border border-border rounded shadow-none">
                        <CardHeader className="pb-3">
                            <div className="flex items-center gap-2">
                                <Mail className="h-5 w-5" />
                                <h3 className="text-lg font-semibold">Authentication Method</h3>
                            </div>
                        </CardHeader>
                        <Divider />
                        <CardBody className="space-y-4">
                            <div className="flex items-start gap-4 p-4 bg-success/10 rounded-lg border border-success/20">
                                <CheckCircle2 className="h-6 w-6 text-success flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h4 className="font-semibold text-foreground mb-1">Email OTP Authentication</h4>
                                    <p className="text-sm text-default-600 mb-2">
                                        Your account uses secure One-Time Password (OTP) authentication. 
                                        Each time you log in, a unique code is sent to your email address.
                                    </p>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Mail className="h-4 w-4 text-default-400" />
                                        <span className="text-default-500">Verification codes are sent to:</span>
                                        <span className="font-medium text-foreground">{user?.email}</span>
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Security Recommendations */}
                    <Card className="border-l-4 border-l-primary border border-border rounded shadow-none">
                        <CardBody className="p-4">
                            <div className="flex items-start gap-3">
                                <Shield className="h-5 w-5 text-primary mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-foreground mb-1">Security Best Practices</h4>
                                    <ul className="text-sm text-default-600 space-y-1 list-disc list-inside">
                                        <li>Keep your email account secure with a strong password</li>
                                        <li>Never share OTP codes with anyone</li>
                                        <li>OTP codes expire after a few minutes for your security</li>
                                        <li>If you didn't request an OTP, someone may be trying to access your account</li>
                                        <li>Contact support if you notice any suspicious activity</li>
                                    </ul>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </main>
        </div>
    )
}
